"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { EventPageClient } from "./EventPageClient";
import type { LoadedEvent } from "./eventTypes";
import { createConfiguredEventsClient, fetchEventDetail } from "@/data/events";
import { buildCanonicalEventPath } from "@/utils/eventSlug";
import { staticPathParam } from "@/lib/staticPathParam";
import { readTrustedEventBootstrap } from "@/lib/eventBootstrap";

/**
 * Reads the real event slug from the browser path.
 * nginx may serve the `__` static shell while the URL still contains the event id.
 */
export function eventSlugFromPath(pathname: string | null): string {
  return staticPathParam(pathname, /^\/events\/([^/]+)\/?$/);
}

/**
 * Loads one public event in the browser and hydrates the existing detail UI.
 * Direct generated-page loads may use trusted `#gruv-event-bootstrap` metadata
 * to skip the route-resolver request. SPA navigation continues using the resolver.
 * Historical rename aliases correct to the current public_slug while preserving
 * the query string (including ?promoter=).
 */
function EventDetailLoader() {
  const t = useTranslations("events");
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const slug = useMemo(() => eventSlugFromPath(pathname), [pathname]);
  const [event, setEvent] = useState<LoadedEvent | null>(null);
  const [error, setError] = useState<"missing" | "not_found" | "failed" | null>(null);

  useEffect(() => {
    if (!slug) {
      setError("missing");
      setEvent(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setEvent(null);
    const client = createConfiguredEventsClient();
    const search = searchParams?.toString() ? `?${searchParams.toString()}` : "";

    const load = async () => {
      let eventId: string;
      const bootstrap = readTrustedEventBootstrap(slug);
      if (bootstrap) {
        eventId = bootstrap.event_id;
      } else {
        const resolved = await client.resolveEventRoute({ routeKey: slug });
        if (!resolved.ok) {
          if (!cancelled) {
            setError(resolved.status === 404 ? "not_found" : "failed");
          }
          return;
        }
        eventId = resolved.data.event_id;
        if (!resolved.data.is_current && resolved.data.public_slug) {
          const next = buildCanonicalEventPath(resolved.data.public_slug, search);
          router.replace(next);
        }
      }

      const response = await fetchEventDetail(eventId, client);
      if (cancelled) return;
      if (!response.ok) {
        setError(response.status === 404 ? "not_found" : "failed");
        return;
      }
      const detail = response.data;
      if (detail.public_slug && detail.public_slug !== slug) {
        router.replace(buildCanonicalEventPath(detail.public_slug, search));
      }
      setEvent(detail);
    };

    void load().catch(() => {
      if (!cancelled) setError("failed");
    });
    return () => {
      cancelled = true;
    };
  }, [slug, router, searchParams]);

  if (error === "missing" || error === "not_found") {
    return (
      <div className="gruv-page">
        <h1 className="gruv-page__title">{t("detail.errors.notFound")}</h1>
      </div>
    );
  }
  if (error === "failed") {
    return (
      <div className="gruv-page">
        <p>{t("detail.about.error", { error: "network" })}</p>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="gruv-page">
        <p>{t("detail.loading")}</p>
      </div>
    );
  }

  const heroImageUrl = event.hero
    ? `/api/events/${event.id}/assets/${event.hero.id}/image`
    : null;

  return (
    <EventPageClient
      slug={slug}
      initialEvent={event}
      initialHeroImageUrl={heroImageUrl}
    />
  );
}

/**
 * Static-export event detail shell with Suspense for search-param consumers.
 */
export function EventDetailPageClient() {
  return (
    <Suspense
      fallback={
        <div className="gruv-page">
          <p>Cargando evento...</p>
        </div>
      }
    >
      <EventDetailLoader />
    </Suspense>
  );
}
